import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../../components/ListPage";
import useFinanceCod from "../../hooks/useFinanceCod";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import { FinanceTabs, formatCurrency, textOrEmpty } from "../shared";

// Finance COD tab (§5). Row entity is `payments`, not `userOrders` — an order paid
// partly UPI and partly COD contributes only its COD payment line here (product-owner
// decision, §5). Column 5 ("Total Amount Paid") is the LOCKED formula
// (SUM(payments.amount) WHERE status='SUCCESS' for the resolved order) — see
// classes/Payment/Functions.js for the known conflict this produces against real dev
// data, flagged separately to the product owner; not something this page can fix.
// Column 8 ("Sales Assigned") is deliberately deferred (§5) — kept in the layout,
// rendered "—".
const PARAM = { page: "page" };

function FinanceCodPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const query = useFinanceCod({ page, size: DEFAULT_PAGE_SIZE });
    const pagination = query.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };
    const rows = query.data?.payments ?? [];

    const columns = useMemo(
        () => [
            {
                key: "orderId",
                label: "Order ID",
                render: (row) => textOrEmpty(row.financeOrderId ?? row.financeQuotationId),
            },
            {
                key: "name",
                label: "Name",
                render: (row) => textOrEmpty(row.paymentItems?.[0]?.userName),
            },
            {
                key: "phone",
                label: "Phone Number",
                render: (row) => textOrEmpty(row.paymentItems?.[0]?.userPhone),
            },
            {
                key: "financeInvoiceTotal",
                label: "Total Invoice Amount",
                align: "right",
                render: (row) => formatCurrency(row.financeInvoiceTotal),
            },
            {
                key: "financeAmountPaid",
                label: "Total Amount Paid",
                align: "right",
                render: (row) => formatCurrency(row.financeAmountPaid),
            },
            {
                key: "financeCodAmount",
                label: "COD Amount To Be Collected",
                align: "right",
                render: (row) => formatCurrency(row.financeCodAmount),
            },
            { key: "status", label: "Status Of COD" },
            {
                key: "salesAssigned",
                label: "Sales Assigned To The Order",
                render: () => textOrEmpty(null),
            },
        ],
        []
    );

    return (
        <>
            <FinanceTabs />
            <ListPage
                testId="finance-cod"
                title="COD"
                subtitle="Cash-on-delivery payments awaiting collection. One row per COD payment, not per order."
                columns={columns}
                rows={rows}
                query={query}
                pagination={pagination}
                onPageChange={(nextPage) => {
                    const next = new URLSearchParams(searchParams);
                    next.set(PARAM.page, String(nextPage));
                    setSearchParams(next, { replace: true });
                }}
                emptyTitle="No COD payments"
                emptyHint="No cash-on-delivery payments found for this page."
            />
        </>
    );
}

export default FinanceCodPage;
